'use client';

import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView, useMotionValue, useSpring, useMotionTemplate } from 'framer-motion';
import { ShoppingBag, Heart, Star, Sparkles, UserCheck, ArrowUpRight, Zap, Smartphone, ShieldCheck, Palette } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

function cn(...inputs: ClassValue[]) {
      return twMerge(clsx(inputs));
}

const Counter = ({ value, label, suffix = "" }: { value: number; label: string; suffix?: string }) => {
      const ref = useRef(null);
      const isInView = useInView(ref, { once: true, margin: "-50px" });
      const motionValue = useMotionValue(0);
      const springValue = useSpring(motionValue, { damping: 30, stiffness: 100 });
      const [displayValue, setDisplayValue] = useState(0);

      useEffect(() => {
            if (isInView) {
                  motionValue.set(value);
            }
      }, [isInView, value, motionValue]);

      useEffect(() => {
            springValue.on("change", (latest) => {
                  setDisplayValue(Math.floor(latest));
            });
      }, [springValue]);

      return (
            <div ref={ref} className="flex flex-col items-center justify-center text-center">
                  <div className="flex items-baseline gap-1">
                        <span className="text-4xl md:text-5xl font-black text-gray-900 tracking-tight tabular-nums">
                              {displayValue.toLocaleString()}
                        </span>
                        <span className="text-3xl font-semibold text-transparent bg-clip-text bg-linear-to-r from-[#6320EE] to-[#DA38E2]">
                              {suffix}
                        </span>
                  </div>
                  <span className="text-sm font-bold text-gray-500 mt-2 uppercase tracking-widest">{label}</span>
            </div>
      );
};

const Divider = () => (
      <div className="hidden md:block w-px h-16 bg-linear-to-b from-transparent via-gray-300 to-transparent mx-8 lg:mx-12 opacity-50" />
);

const BentoCard = ({ children, className, delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) => {
      const mouseX = useMotionValue(0);
      const mouseY = useMotionValue(0);

      function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
            const { left, top } = currentTarget.getBoundingClientRect();
            mouseX.set(clientX - left);
            mouseY.set(clientY - top);
      }

      return (
            <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay, ease: "easeOut" }}
                  className={cn(
                        "group relative border border-gray-200 bg-white overflow-hidden rounded-3xl",
                        className
                  )}
                  onMouseMove={handleMouseMove}
            >
                  <motion.div
                        className="pointer-events-none absolute -inset-px rounded-3xl opacity-0 transition duration-300 group-hover:opacity-100"
                        style={{
                              background: useMotionTemplate`
              radial-gradient(
                650px circle at ${mouseX}px ${mouseY}px,
                rgba(218, 56, 226, 0.15),
                transparent 80%
              )
            `,
                        }}
                  />
                  <div className="relative h-full">{children}</div>
            </motion.div>
      );
};

const StatsAndFeatures = () => {
      return (
            <section className="relative w-full pb-0 bg-[#FAFAFA] overflow-visible">

                  <div className="absolute inset-0 bg-[url('/noise.png')] opacity-[0.03] pointer-events-none mix-blend-multiply"></div>
                  <div className="absolute left-[-20%] top-[10%] w-[50%] h-[50%] bg-purple-300/10 rounded-full blur-[120px] pointer-events-none" />

                  <div className="container mx-auto px-6 relative z-10">

                        <motion.div
                              initial={{ y: 30, opacity: 0 }}
                              whileInView={{ y: 0, opacity: 1 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.8 }}
                              className="relative z-40 mx-auto max-w-7xl pt-10 md:-mt-8"
                        >
                              {/* <div className="flex flex-col md:flex-row items-center justify-center gap-y-12 md:gap-y-0">
                                    <Counter value={1265} label="Aktif Kullanıcı" />
                                    <Divider />
                                    <Counter value={15} label="Partner Marka" suffix="+" />
                                    <Divider />
                                    <Counter value={508} label="Günlük Etkileşim" suffix="K" />
                                    <Divider />
                                    <div className="flex flex-col items-center justify-center text-center">
                                          <div className="flex items-center gap-1 mb-1">
                                                {[1, 2, 3, 4, 5].map(i => (
                                                      <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                                                ))}
                                          </div>
                                          <div className="flex items-baseline gap-2 mt-1">
                                                <span className="text-3xl font-black text-gray-900">4.9</span>
                                                <span className="text-sm font-medium text-gray-400">/ 5</span>
                                          </div>
                                          <span className="text-sm font-bold text-gray-500 mt-2 uppercase tracking-widest">Trustpilot</span>
                                    </div>
                              </div> */}
                        </motion.div>

                        <div className="mt-24 md:mt-32 flex flex-col items-center">
                              <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    className="text-center max-w-2xl mb-16"
                              >
                                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 tracking-tight">
                                          Kadınların Dünyası, <br />
                                          <span className="bg-clip-text text-transparent bg-linear-to-r from-[#6320EE] to-[#DA38E2]">Tek Bir Uygulamada</span>
                                    </h2>
                                    <p className="text-gray-600 text-lg leading-relaxed">
                                          Gyno Club; alışverişten sosyal yaşama, yeme içmeden wellness&apos;a kadar kadınların hayatındaki her ana değer katan, size özel bir ayrıcalık ekosistemidir.
                                    </p>
                              </motion.div>

                              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-6xl h-auto md:h-150">

                                    <BentoCard className="md:col-span-1 md:row-span-2 bg-linear-to-b from-white to-gray-50 flex flex-col justify-between p-8">
                                          <div>
                                                <div className="w-14 h-14 bg-purple-100 rounded-2xl flex items-center justify-center mb-6 text-purple-600">
                                                      <ShoppingBag className="w-7 h-7" />
                                                </div>
                                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Ayrıcalıklı Alışveriş</h3>
                                                <p className="text-gray-500 leading-relaxed text-sm">
                                                      Sevdiğiniz markalarda Gyno Club üyelerine özel tanımlanan indirimler ve sürpriz hediyelerle alışveriş keyfinizi ikiye katlayın.
                                                </p>
                                          </div>
                                          <div className="mt-8 relative w-full h-40 bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden p-4">
                                                <div className="flex items-center gap-2 mb-2">
                                                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                                      <span className="text-xs text-green-600 font-bold">Aktif Kampanyalar</span>
                                                </div>
                                                <div className="space-y-2">
                                                      <div className="h-2 w-3/4 bg-gray-100 rounded-full" />
                                                      <div className="h-2 w-1/2 bg-gray-100 rounded-full" />
                                                      <div className="h-2 w-full bg-gray-100 rounded-full" />
                                                </div>
                                          </div>
                                    </BentoCard>

                                    <BentoCard className="md:col-span-2 md:row-span-1 bg-gray-900 text-white overflow-hidden" delay={0.1}>
                                          <div className="absolute inset-0 bg-linear-to-r from-purple-900/50 to-pink-900/50 mix-blend-overlay z-0" />
                                          <div className="relative z-10 flex flex-col md:flex-row h-full">
                                                <div className="p-8 md:w-1/2 flex flex-col justify-center">
                                                      <div className="flex items-center gap-2 text-purple-300 mb-2">
                                                            <Sparkles className="w-5 h-5" />
                                                            <span className="text-sm font-bold uppercase tracking-wider">Premium Deneyim</span>
                                                      </div>
                                                      <h3 className="text-2xl md:text-3xl font-bold mb-4">Sosyal Yaşam & Eğlence</h3>
                                                      <p className="text-gray-300 text-sm leading-relaxed mb-6">
                                                            Şehrin en popüler kafe ve restoranlarında yeriniz hazır. Rezervasyon önceliği ve size özel ikramlarla kendinizi özel hissedin.
                                                      </p>
                                                      <button className="flex items-center gap-2 text-sm font-bold hover:text-purple-300 transition-colors w-fit">
                                                            Mekanları İncele <ArrowUpRight className="w-4 h-4" />
                                                      </button>
                                                </div>
                                                <div className="md:w-1/2 h-48 md:h-full relative mt-4 md:mt-0">
                                                      <div className="absolute right-0 top-8 md:top-12 w-[90%] h-full bg-white/10 backdrop-blur-md rounded-tl-2xl border-l border-t border-white/20 p-4 transform translate-x-4 hover:translate-x-2 transition-transform duration-500">
                                                            <div className="flex items-center gap-3 mb-4">
                                                                  <div className="w-8 h-8 rounded-full bg-pink-500" />
                                                                  <div className="w-20 h-3 bg-white/20 rounded-full" />
                                                            </div>
                                                            <div className="space-y-3">
                                                                  <div className="w-full h-24 bg-linear-to-r from-purple-500 to-pink-500 rounded-lg opacity-80" />
                                                            </div>
                                                      </div>
                                                </div>
                                          </div>
                                    </BentoCard>

                                    <BentoCard className="md:col-span-1 md:row-span-1 p-8 flex flex-col justify-center" delay={0.2}>
                                          <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center mb-4 text-orange-600">
                                                <Heart className="w-6 h-6" />
                                          </div>
                                          <h3 className="text-xl font-bold text-gray-900 mb-2">Sağlık & Wellness</h3>
                                          <p className="text-gray-500 text-sm">
                                                Güzellik merkezlerinden spor salonlarına, sağlıklı yaşam için ihtiyacınız olan her yerde ayrıcalıklısınız.
                                          </p>
                                    </BentoCard>

                                    <BentoCard className="md:col-span-1 md:row-span-1 p-8 bg-linear-to-br from-[#F4F1FF] to-white" delay={0.3}>
                                          <div className="flex justify-between items-start mb-4">
                                                <div className="w-12 h-12 bg-white rounded-xl shadow-sm flex items-center justify-center text-[#6320EE]">
                                                      <UserCheck className="w-6 h-6" />
                                                </div>
                                                <span className="px-3 py-1 bg-white rounded-full text-xs font-bold text-gray-600 shadow-sm border border-gray-100">
                                                      Size Özel
                                                </span>
                                          </div>
                                          <h3 className="text-xl font-bold text-gray-900 mb-2">Kişiselleştirilmiş</h3>
                                          <p className="text-gray-500 text-sm">
                                                İlgi alanlarınıza ve alışkanlıklarınıza göre şekillenen, size en uygun fırsatları sunan akıllı bir asistan.
                                          </p>
                                    </BentoCard>
                              </div>
                        </div>
                  </div>
            </section>
      );
};

export default StatsAndFeatures;